import { chunk } from 'lodash';

import type { HotelAmenityType } from '@libs/trip-planner-interfaces';

import type { Distance, HotelStaticData } from '../models';
import type { HotelAmenityData } from '../../../core/models';

export function getTagsUrls(
  hotelDetails: HotelStaticData | undefined
): string[] | undefined {
  const tagIconUrls = hotelDetails?.staticInfo.tagIconUrls;

  return tagIconUrls?.length ? tagIconUrls : undefined;
}

export function getOpeningParagraph(
  hotelDetails: HotelStaticData | undefined
): string | undefined {
  return hotelDetails?.staticInfo.openingParagraph?.value;
}

// NOTE:
// In collapsed version of hotel description we want to show more or less the amount of text
// equal to height of map in the other column. Database content is not consistent (HTML, Markdown, optional fields),
// so calculating this exactly is hard. Fuzzy logic below is an approximation.
export function getMainDescription(
  hotelDetails: HotelStaticData | undefined,
  showFullDescription: boolean,
  tagsUrls: string[] | undefined,
  openingParagraph: string | undefined,
  charactersConfig: { tagsEquivalent: number; toFillSpaceNextToMap: number }
): string | undefined {
  const description = hotelDetails?.staticInfo.description?.value;

  if (!description) {
    return;
  }

  let descriptionLength = charactersConfig.toFillSpaceNextToMap;

  if (tagsUrls?.length) {
    descriptionLength -= charactersConfig.tagsEquivalent;
  }

  if (openingParagraph) {
    descriptionLength -= openingParagraph.length;
  }

  return showFullDescription
    ? description
    : trimDescription(description, descriptionLength);

  function trimDescription(
    fullDescription: string,
    allowedCharacters: number
  ): string {
    return `${fullDescription.slice(0, allowedCharacters)}...`;
  }
}

export function getDistances(
  hotelDetails: HotelStaticData | undefined
): Distance[] | undefined {
  const distances = Object.values(hotelDetails?.staticInfo.distances ?? {});

  if (!distances?.length) {
    return;
  }

  return distances
    .map((distance) => ({
      description: distance.description,
      distanceMeters: distance.value,
    }))
    .sort((a, b) => a.distanceMeters - b.distanceMeters);
}

export function getOfferedAmenitiesWithConfig(
  hotelDetails: HotelStaticData | undefined,
  hotelAmenitiesConfig: Record<HotelAmenityType, HotelAmenityData>,
  logMissingAmenity: (unknownAmenityName: string) => void
): HotelAmenityData[] | undefined {
  const amenities = hotelDetails?.staticInfo.amenities;

  if (!amenities) {
    return;
  }

  const allAmenitiesTypes = Object.keys(amenities) as HotelAmenityType[];
  const offeredAmenities: HotelAmenityType[] = allAmenitiesTypes.filter(
    (amenity: HotelAmenityType) => !!amenities[amenity]
  );

  return offeredAmenities.reduce(
    (accumulator: HotelAmenityData[], current: HotelAmenityType) => {
      if (hotelAmenitiesConfig[current]) {
        return [...accumulator, hotelAmenitiesConfig[current]];
      } else {
        logMissingAmenity(current);
      }
      return accumulator;
    },
    []
  );
}

export function getChunkAmenities(
  amenities: HotelAmenityData[] | undefined,
  isSmallDevice: boolean,
  chunkSize: number
): HotelAmenityData[][] | undefined {
  if (!amenities) {
    return;
  }

  return isSmallDevice
    ? [amenities]
    : chunk(amenities, Math.ceil(amenities.length / chunkSize));
}

export function getShortAmenitiesGroups(
  amenities: HotelAmenityData[] | undefined,
  isSmallDevice: boolean,
  chunkSize: number
): HotelAmenityData[][] | undefined {
  if (!amenities) {
    return undefined;
  }

  if (isSmallDevice) {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return [amenities.slice(0, 6)];
  } else {
    const chunked = getChunkAmenities(amenities, isSmallDevice, chunkSize);

    if (!chunked || !chunked[0] || !chunked[1]) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return [chunked[0].slice(0, 6), chunked[1].slice(0, 6)];
  }
}

export function shouldShowMoreAmenities(
  allAmenities: HotelAmenityData[] | undefined,
  shortAmenitiesGroups: HotelAmenityData[][] | undefined
): boolean {
  if (!allAmenities || !shortAmenitiesGroups) {
    return false;
  }

  const flatGroups = shortAmenitiesGroups.flat();
  return allAmenities.length > flatGroups.length;
}
